import { useState, useEffect, useCallback, useMemo, useImperativeHandle, useRef } from "react";
import type {
	FuseAuthProviderComponentProps,
	FuseAuthProviderState,
} from "@fuse/core/FuseAuthProvider/types/FuseAuthTypes";
import type { User } from "@auth/user";
import { setUserServer } from "@auth/user/sessionUtils";
import ContextoAutenticacaoJwt, { type TipoContextoAutenticacaoJwt } from "./ContextoAutenticacaoJwt";
import { mapearRespostaLoginParaUsuario } from "./utils/mapearUsuarioAutenticado";
import { useAutenticarSpace, useAutenticarLoginUnificadoSpace } from "@/api/space/hooks/useSpace.authenticator";
import {
	aplicarSessaoAutenticadaSpaceApi,
	definirCallbackFalhaAutenticacaoSpaceApi,
	limparSessaoAutenticadaSpaceApi,
} from "@/api/space/rootAuth";
import {
	definirDoisFatoresPendente,
	limparLogoutExplicito,
	marcarLogoutExplicito,
} from "@/api/space/estadoAutenticacao.space";

import { useQueryClient } from "@tanstack/react-query";
import { salvarGrupoLembradoLogin } from "./utils/grupoLembradoLogin";
import useRestauracaoSessaoJwt from "./hooks/useRestauracaoSessaoJwt";
import { validarSessaoLoginUnificado } from "@/api/space/control/utils/authenticatorUtils";
import { resetSessionRedirectUrl } from "@fuse/core/FuseAuthorization/sessionRedirectUrl";

// ------------------------------- CONSTANTS -------------------------------
const STATUS_AUTENTICACAO = {
	CONFIGURANDO: "configuring",
	AUTENTICADO: "authenticated",
	NAO_AUTENTICADO: "unauthenticated",
} as const;

// -------------------------------- HELPERS --------------------------------
function limparTokenLegadoLocalStorage(): void {
	try {
		localStorage.removeItem("jwt_access_token");
	} catch {
		// ignore
	}
}

function criarEstadoAutenticado(user: User): FuseAuthProviderState<User> {
	return {
		authStatus: STATUS_AUTENTICACAO.AUTENTICADO,
		isAuthenticated: true,
		user,
	};
}

function criarEstadoNaoAutenticado(): FuseAuthProviderState<User> {
	return {
		authStatus: STATUS_AUTENTICACAO.NAO_AUTENTICADO,
		isAuthenticated: false,
		user: null,
	};
}

// ------------------------------- COMPONENT -------------------------------
function ProvedorAutenticacaoJwt(props: Readonly<FuseAuthProviderComponentProps>) {
	const { ref, children, onAuthStateChanged } = props;

	// -------------------------------- STATES --------------------------------
	const versaoSessaoRef = useRef(0);
	const montadoRef = useRef(false);
	const [estadoAutenticacao, setEstadoAutenticacao] = useState<FuseAuthProviderState<User>>({
		authStatus: STATUS_AUTENTICACAO.CONFIGURANDO,
		isAuthenticated: false,
		user: null,
	});

	// -------------------------------- QUERIES -------------------------------

	const { mutateAsync: login } = useAutenticarSpace();
	const { mutateAsync: loginUnificado } = useAutenticarLoginUnificadoSpace();
	const queryClient = useQueryClient();

	// ------------------------------- HANDLERS -------------------------------
	const aplicarUsuarioAutenticado = useCallback((user: User) => {
		setUserServer(user);
		setEstadoAutenticacao(criarEstadoAutenticado(user));
	}, []);

	const desautenticarSessao = useCallback(() => {
		versaoSessaoRef.current += 1;
		setUserServer(null);
		limparTokenLegadoLocalStorage();
		limparSessaoAutenticadaSpaceApi();
		queryClient.clear(); // limpa cache de todas as queries
		setEstadoAutenticacao(criarEstadoNaoAutenticado());
	}, [queryClient]);

	const entrar: TipoContextoAutenticacaoJwt["entrar"] = useCallback(
		async (credentials) => {
			const versaoSessao = ++versaoSessaoRef.current;
			const sessao = await login(credentials);
			if (!montadoRef.current || versaoSessao !== versaoSessaoRef.current) return null;
			const accessToken = sessao.token;
			const requerDoisFatores = Boolean(sessao.autenticacao_dois_fatores);
			if (!accessToken) throw new Error("Login não retornou token.");

			const user = mapearRespostaLoginParaUsuario(sessao, {
				groupIdPadrao: credentials.groupId,
			});

			if (requerDoisFatores) {
				desautenticarSessao();
				return {
					status: "2fa_required" as const,
					user,
					access_token: accessToken,
					groupId: credentials.groupId,
					username: credentials.username,
					id_cad_pessoa_funcionario: sessao.id_cad_pessoa_funcionario ?? null,
				};
			}

			aplicarUsuarioAutenticado(user);
			limparLogoutExplicito();
			aplicarSessaoAutenticadaSpaceApi({
				token: accessToken,
				groupId: credentials.groupId,
			});

			return {
				status: "authenticated" as const,
				user,
				access_token: accessToken,
			};
		},
		[login, aplicarUsuarioAutenticado, desautenticarSessao],
	);

	const concluirEntrarDoisFatores: TipoContextoAutenticacaoJwt["concluirEntrarDoisFatores"] = useCallback(
		({ user, access_token, groupId }) => {
			versaoSessaoRef.current += 1;
			aplicarUsuarioAutenticado(user);
			limparLogoutExplicito();
			aplicarSessaoAutenticadaSpaceApi({
				token: access_token,
				groupId,
			});
		},
		[aplicarUsuarioAutenticado],
	);

	const sair: TipoContextoAutenticacaoJwt["sair"] = useCallback(() => {
		marcarLogoutExplicito();
		resetSessionRedirectUrl();
		definirDoisFatoresPendente(false);
		desautenticarSessao();
	}, [desautenticarSessao]);

	const concluirEntrarUnificado: TipoContextoAutenticacaoJwt["concluirEntrarUnificado"] = useCallback(
		({ resposta, identificacao, signal, versaoSessao }) => {
			if (signal.aborted || !montadoRef.current || versaoSessao !== versaoSessaoRef.current) return false;
			validarSessaoLoginUnificado(resposta);

			versaoSessaoRef.current += 1;
			const groupId = String(resposta.grupo.id);
			const user = mapearRespostaLoginParaUsuario(resposta, { groupIdPadrao: groupId });
			definirDoisFatoresPendente(false);
			limparLogoutExplicito();
			aplicarSessaoAutenticadaSpaceApi({ token: resposta.token, groupId });
			salvarGrupoLembradoLogin({ grupo: resposta.grupo, identificacao });
			aplicarUsuarioAutenticado(user);
			return true;
		},
		[aplicarUsuarioAutenticado],
	);

	const entrarUnificado: TipoContextoAutenticacaoJwt["entrarUnificado"] = useCallback(
		async ({ payload, identificacao, signal }) => {
			const versaoSessao = ++versaoSessaoRef.current;
			const resultado = await loginUnificado({ payload, signal });
			if (signal.aborted || !montadoRef.current || versaoSessao !== versaoSessaoRef.current) return null;
			if (payload.senha === undefined && resultado.status === 200)
				throw new Error(
					"Não foi possível iniciar o OTP: o login SPACE sem senha deve retornar um desafio (202).",
				);
			if (resultado.status === 200)
				concluirEntrarUnificado({ resposta: resultado.dados, identificacao, signal, versaoSessao });
			else definirDoisFatoresPendente(true);
			return { ...resultado, versaoSessao };
		},
		[loginUnificado, concluirEntrarUnificado],
	);

	// -------------------------------- EFFECTS --------------------------------
	useRestauracaoSessaoJwt({
		status: estadoAutenticacao.authStatus,
		versaoSessaoRef,
		montadoRef,
		aoAutenticar: aplicarUsuarioAutenticado,
		aoDesautenticar: desautenticarSessao,
	});

	useEffect(() => {
		montadoRef.current = true;
		return () => {
			montadoRef.current = false;
		};
	}, []);

	useEffect(() => {
		onAuthStateChanged?.(estadoAutenticacao);
	}, [estadoAutenticacao, onAuthStateChanged]);

	useImperativeHandle(
		ref,
		() => ({
			signOut: sair,
			updateUser: () => {
				throw new Error("updateUser is not implemented");
			},
		}),
		[sair],
	);

	useEffect(() => {
		definirCallbackFalhaAutenticacaoSpaceApi(sair);
		return () => definirCallbackFalhaAutenticacaoSpaceApi(null);
	}, [sair]);

	// -------------------------------- PROPS ---------------------------------
	const valorContexto = useMemo<TipoContextoAutenticacaoJwt>(
		() => ({
			...estadoAutenticacao,
			entrar,
			concluirEntrarDoisFatores,
			entrarUnificado,
			concluirEntrarUnificado,
			sair,
		}),
		[estadoAutenticacao, entrar, concluirEntrarDoisFatores, entrarUnificado, concluirEntrarUnificado, sair],
	);

	// -------------------------------- RETURN --------------------------------
	return <ContextoAutenticacaoJwt value={valorContexto}>{children}</ContextoAutenticacaoJwt>;
}

export default ProvedorAutenticacaoJwt;
export { type CredenciaisEntrarJwt } from "./tiposAutenticacaoJwt";
